import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  FiCalendar,
  FiChevronDown,
  FiMapPin,
  FiSearch,
  FiUsers,
  FiPlus,
  FiMinus
} from "react-icons/fi";
import { IoSwapHorizontal } from "react-icons/io5";
import { MdFlight } from "react-icons/md";
import "../Book/Book.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const Book = ({ onSearch, initialSearchParams }) => {
  const navigate = useNavigate();
  
  // Calculate initial passenger count from URL params or sessionStorage
  const getInitialPassengerCount = () => {
    // First check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const urlAdults = urlParams.get('adults');
    const urlChildren = urlParams.get('children');
    const urlInfants = urlParams.get('infants');
    
    if (urlAdults || urlChildren || urlInfants) {
      return {
        adults: parseInt(urlAdults) || 1,
        children: parseInt(urlChildren) || 0,
        infants: parseInt(urlInfants) || 0
      };
    }
    
    // Then check initialSearchParams
    if (initialSearchParams?.passengerDetails) {
      return initialSearchParams.passengerDetails;
    }
    
    // Then check sessionStorage
    try {
      const storedParams = sessionStorage.getItem("flightSearchParams");
      if (storedParams) {
        const parsed = JSON.parse(storedParams);
        if (parsed.passengerDetails) {
          return parsed.passengerDetails;
        }
        // Fallback to travelers count
        if (parsed.travelers) {
          return {
            adults: Math.min(parsed.travelers, 9),
            children: 0,
            infants: 0
          };
        }
      }
    } catch (error) {
      console.error("Error reading sessionStorage:", error);
    }
    
    // Default
    return {
      adults: 1,
      children: 0,
      infants: 0
    };
  };

  // Helper function to add one day to a date
  const addOneDay = (date) => {
    if (!date) return null;
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextDay;
  };

  // State management - UPDATED: Track if default should be shown
  const [shouldShowDefault, setShouldShowDefault] = useState(
    !initialSearchParams?.originName && !initialSearchParams?.origin
  );
  
  const [tripType, setTripType] = useState(
    initialSearchParams?.tripType === "one-way" ? "one-way" : "round"
  );
  
  // UPDATED: Show default only if no initial params AND default hasn't been cleared
  const [from, setFrom] = useState(
    initialSearchParams?.originName || 
    (shouldShowDefault ? "London Heathrow Airport" : "")
  );
  
  // FIXED: Initialize with initialSearchParams if available
  const [to, setTo] = useState(initialSearchParams?.destinationName || "");
  
  // UPDATED: Show LON code only if no initial params AND default hasn't been cleared
  const [fromAirportCode, setFromAirportCode] = useState(
    initialSearchParams?.origin || 
    (shouldShowDefault ? "LON" : "")
  );
  
  // FIXED: Initialize with initialSearchParams if available
  const [toAirportCode, setToAirportCode] = useState(initialSearchParams?.destination || "");
  
  const [departDate, setDepartDate] = useState(
    initialSearchParams?.departureDate
      ? new Date(initialSearchParams.departureDate)
      : new Date()
  );
  const [returnDate, setReturnDate] = useState(
    initialSearchParams?.returnDate
      ? new Date(initialSearchParams.returnDate)
      : null
  );
  
  // UPDATED: Passenger state with adults, children, infants - Read from URL params
  const [passengerCount, setPassengerCount] = useState(getInitialPassengerCount());
  
  const [isPassengerDropdownOpen, setIsPassengerDropdownOpen] = useState(false);
  
  const [departSuggestions, setDepartSuggestions] = useState([]);
  const [arrivalSuggestions, setArrivalSuggestions] = useState([]);
  const [error, setError] = useState("");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [apiFailed, setApiFailed] = useState(false);

  // Track if user has interacted with From input
  const [fromInputTouched, setFromInputTouched] = useState(false);

  // Use separate refs for each dropdown
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);
  const departDateRef = useRef(null);
  const returnDateRef = useRef(null);
  const suggestionsRef = useRef(null);
  const passengerDropdownRef = useRef(null);

  // Create refs for DatePicker
  const departDatePickerRef = useRef(null);
  const returnDatePickerRef = useRef(null);

  // Calculate total travelers
  const totalTravelers = passengerCount.adults + passengerCount.children + passengerCount.infants;

  // Close dropdowns when clicking outside - FIXED
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on suggestions dropdown
      if (
        suggestionsRef.current &&
        suggestionsRef.current.contains(event.target)
      ) {
        return;
      }

      // Don't close if clicking on passenger dropdown
      if (
        passengerDropdownRef.current &&
        passengerDropdownRef.current.contains(event.target)
      ) {
        return;
      }

      // Don't close if clicking on datepicker
      if (
        event.target.closest(".react-datepicker") ||
        event.target.closest(".react-datepicker__input-container") ||
        event.target.closest(".custom-date-input-bo")
      ) {
        return;
      }

      // Close suggestions if clicking outside input and suggestions
      if (
        openDropdown &&
        !event.target.closest(".location-input-bo") &&
        !event.target.closest(".suggestions-dropdown-bo")
      ) {
        setOpenDropdown(null);
        setDepartSuggestions([]);
        setArrivalSuggestions([]);
      }

      // Close passenger dropdown if clicking outside
      if (
        isPassengerDropdownOpen &&
        !event.target.closest(".passenger-dropdown-container-bo") &&
        !event.target.closest(".passenger-input-bo")
      ) {
        setIsPassengerDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdown, isPassengerDropdownOpen]);

  // UPDATED: Airport search - Now async API call with query param
  const fetchLocations = useCallback(async (query, setSuggestions) => {
    if (query.length < 2) {
      // Min 2 chars for better perf
      setSuggestions([]);
      return;
    }

    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_BASE_URL
        }/airports/search?search=${encodeURIComponent(query)}`
      );

      if (
        response?.data?.success &&
        Array.isArray(response.data?.data) &&
        response.data?.data?.length > 0
      ) {
        // Slice to top 5 for display
        const limitedSuggestions = response.data.data.slice(0, 5);
        setSuggestions(limitedSuggestions);
        setApiFailed(false);
      } else {
        setSuggestions([]);
        if (!apiFailed) {
          toast.error("No airports found. Try a different query.");
        }
      }
    } catch (err) {
      console.error("Error fetching airports:", err.message);
      setSuggestions([]);
      setApiFailed(true);
      toast.error("Search failed. Please try again");
    }
  }, []);

  // Debounce function
  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedFetchLocations = useCallback(debounce(fetchLocations, 300), [
    fetchLocations,
  ]);

  // Function to generate shareable URL with passenger details
  const generateShareableUrl = (searchData) => {
    const params = new URLSearchParams();
    params.set("origin", searchData.origin);
    params.set("destination", searchData.destination);
    params.set("departureDate", searchData.departureDate);
    if (searchData.returnDate) params.set("returnDate", searchData.returnDate);
    params.set("travelers", searchData.travelers.toString());
    params.set("tripType", searchData.tripType);
    params.set("travelClass", searchData.travelClass || "Economy");
    params.set("originName", searchData.originName);
    params.set("destinationName", searchData.destinationName);
    
    // Add passenger details to URL
    params.set("adults", passengerCount.adults.toString());
    params.set("children", passengerCount.children.toString());
    params.set("infants", passengerCount.infants.toString());
    
    return `${window.location.origin}/flight-result?${params.toString()}`;
  };

  // Input handlers - UPDATED: Clear default when user types
  const handleFromChange = (e) => {
    const value = e.target.value;
    setFrom(value);
    
    // Clear default when user starts typing
    if (shouldShowDefault && value !== "London Heathrow Airport") {
      setShouldShowDefault(false);
      setFromAirportCode("");
    }
    
    setFromInputTouched(true);

    if (value.length > 0) {
      setOpenDropdown("from");
      setArrivalSuggestions([]);
      debouncedFetchLocations(value, setDepartSuggestions);
    } else {
      setOpenDropdown(null);
      setDepartSuggestions([]);
    }
  };

  const handleToChange = (e) => {
    const value = e.target.value;
    setTo(value);
    
    // Clear the airport code when user starts typing a completely new value
    // This ensures if the value came from API/other page, it gets cleared
    if (initialSearchParams?.destinationName && value !== initialSearchParams.destinationName) {
      setToAirportCode("");
    }

    if (value.length > 0) {
      setOpenDropdown("to");
      setDepartSuggestions([]);
      debouncedFetchLocations(value, setArrivalSuggestions);
    } else {
      setOpenDropdown(null);
      setArrivalSuggestions([]);
    }
  };

  // Handle From input click - clear entire value when clicked for editing
  const handleFromClick = () => {
    // Clear the entire value when first clicked for editing
    if (!fromInputTouched) {
      setFrom("");
      setFromAirportCode("");
      setShouldShowDefault(false);
      setFromInputTouched(true);
    } else {
      // If already touched, keep the current value but allow full replacement
      // The input will keep the value but user can overwrite it completely
    }

    setOpenDropdown("from");
    if (from.length > 1) {
      fetchLocations(from, setDepartSuggestions);
    }
  };

  // Handle To input click - clear entire value when clicked for editing
  const handleToClick = () => {
    // Clear the entire value when clicked if it came from API or other page
    // We check if the value matches what would come from initialSearchParams
    if (initialSearchParams?.destinationName && to === initialSearchParams.destinationName) {
      setTo("");
      setToAirportCode("");
    }
    
    setOpenDropdown("to");
    if (to.length > 1) {
      fetchLocations(to, setArrivalSuggestions);
    }
  };

  // UPDATED: Handle airport selection - ensure default is cleared
  const handleAirportSelect = (airport, type) => {
    if (type === "departure") {
      setFrom(airport.name);
      setFromAirportCode(airport.code);
      setDepartSuggestions([]);
      setShouldShowDefault(false); // Ensure default is cleared
      setFromInputTouched(true);
    } else {
      setTo(airport.name);
      setToAirportCode(airport.code);
      setArrivalSuggestions([]);
    }
    setOpenDropdown(null);
  };

  // Date handling
  const handleDepartDateChange = (date) => {
    const adjustedDate = new Date(date);
    adjustedDate.setHours(12, 0, 0, 0);
    setDepartDate(adjustedDate);
    if (returnDate && returnDate < adjustedDate) {
      setReturnDate(null);
    }
  };

  const handleReturnDateChange = (date) => {
    const adjustedDate = new Date(date);
    adjustedDate.setHours(12, 0, 0, 0);
    setReturnDate(adjustedDate);
  };

  // Passenger handlers
  const updatePassengerCount = (type, operation) => {
    setPassengerCount(prev => {
      const newCount = { ...prev };
      
      if (operation === 'increase') {
        // Check total limit (max 9 total passengers)
        const total = prev.adults + prev.children + prev.infants;
        if (total >= 9) return prev;
        
        if (type === 'adults') {
          if (prev.adults < 9) newCount.adults++;
        } else if (type === 'children') {
          if (prev.children < 9) newCount.children++;
        } else if (type === 'infants') {
          // Infants cannot exceed number of adults
          if (prev.infants < prev.adults && prev.infants < 9) newCount.infants++;
        }
      } else if (operation === 'decrease') {
        if (type === 'adults') {
          // Cannot have less than 1 adult
          if (prev.adults > 1) {
            newCount.adults--;
            // If infants exceed new adult count, reduce infants
            if (prev.infants > newCount.adults) {
              newCount.infants = newCount.adults;
            }
          }
        } else if (type === 'children') {
          if (prev.children > 0) newCount.children--;
        } else if (type === 'infants') {
          if (prev.infants > 0) newCount.infants--;
        }
      }
      
      return newCount;
    });
  };

  // FIXED: Swap locations - Handle default state properly
  const swapLocations = () => {
    const tempFrom = from;
    const tempFromCode = fromAirportCode;
    const tempTo = to;
    const tempToCode = toAirportCode;
    
    setFrom(tempTo);
    setFromAirportCode(tempToCode);
    setTo(tempFrom);
    setToAirportCode(tempFromCode);
    
    // Handle default state properly
    if (tempTo === "" && fromAirportCode === "LON") {
      setShouldShowDefault(true);
    } else if (shouldShowDefault && tempTo !== "") {
      setShouldShowDefault(false);
    }
  };

  // UPDATED: Search handling - Generate shareable URL with passenger details
  const handleSearch = async () => {
    if (!fromAirportCode || !toAirportCode) {
      setError("Please select valid departure and arrival airports");
      return;
    }

    if (tripType === "round" && !returnDate) {
      setError("Please select a return date");
      return;
    }

    setError("");
    setIsSearching(true);

    const formatDateForAPI = (date) => {
      if (!date) return null;
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;
    };

    const searchData = {
      origin: fromAirportCode,
      originName: from,
      destination: toAirportCode,
      destinationName: to,
      departureDate: formatDateForAPI(departDate),
      returnDate: tripType === "round" ? formatDateForAPI(returnDate) : null,
      travelers: totalTravelers,
      tripType: tripType === "round" ? "round-trip" : "one-way",
      travelClass: "Economy", // Default value
      fromFullName: `${from} (${fromAirportCode})`,
      toFullName: `${to} (${toAirportCode})`,
      departureDateFormatted: departDate.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      returnDateFormatted: returnDate
        ? returnDate.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : null,
      usingDummyData: apiFailed,
      passengerDetails: passengerCount
    };

    // Generate shareable URL with passenger details
    const shareableUrl = generateShareableUrl(searchData);
    console.log("Shareable URL with passenger details:", shareableUrl);
    
    // Store in sessionStorage
    sessionStorage.setItem("flightSearchParams", JSON.stringify(searchData));

    await new Promise((resolve) => setTimeout(resolve, 600));

    if (typeof onSearch === "function") {
      onSearch(searchData);
    }

    setIsSearching(false);
  };

  // Custom date input
  const CustomInput = React.forwardRef(({ value, onClick, disabled }, ref) => (
    <div
      className={`custom-date-input-bo ${disabled ? "disabled-bo" : ""}`}
      onClick={!disabled ? onClick : undefined}
      ref={ref}
      style={{ cursor: disabled ? "not-allowed" : "pointer" }}
    >
      <FiCalendar className="date-icon-bo" />
      <span className="date-value-bo">
        {value
          ? new Date(value).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })
          : "Select"}
      </span>
      {value && <span className="selected-text-bo">Selected</span>}
    </div>
  ));

  // Handle date picker open/close
  const handleDatePickerOpen = () => {
    // Close other dropdowns when date picker opens
    setOpenDropdown(null);
    setDepartSuggestions([]);
    setArrivalSuggestions([]);
    setIsPassengerDropdownOpen(false);
  };

  return (
    <div className="book-container-bo">
      <div className="booking-wrapper-bo">
        {/* API Status Indicator (for debugging) */}
        {apiFailed && (
          <div className="api-status-bo">
            <span className="api-status-text-bo">
              ⚠️ Something went wrong. Contact administration.
            </span>
          </div>
        )}

        {/* Vertical Trip Type Selector */}
        <div className="trip-type-vertical-bo">
          <div className="trip-type-header-bo">
            <MdFlight className="trip-icon-bo" />
            <span>Trip Type</span>
          </div>
          <div className="trip-options-bo">
            <button
              className={`trip-option-bo ${
                tripType === "round" ? "active-bo" : ""
              }`}
              onClick={() => setTripType("round")}
            >
              <div className="trip-label-bo">Round Trip</div>
              {tripType === "round" && (
                <div className="selected-indicator-bo">✓</div>
              )}
            </button>
            <button
              className={`trip-option-bo ${
                tripType === "one-way" ? "active-bo" : ""
              }`}
              onClick={() => setTripType("one-way")}
            >
              <div className="trip-label-bo">One Way</div>
              {tripType === "one-way" && (
                <div className="selected-indicator-bo">✓</div>
              )}
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="main-content-bo">
          {/* Row 1: From, To, and Search Button */}
          <div className="form-row-bo first-row-bo">
            <div className="location-group-bo">
              <div className="location-input-wrapper-bo" ref={suggestionsRef}>
                <div className="input-header-bo">
                  <FiMapPin className="input-icon-bo" />
                  <span className="input-label-bo">From</span>
                  {fromAirportCode && (
                    <span className="code-display-bo">{fromAirportCode}</span>
                  )}
                </div>
                <div className="input-container-bo">
                  <input
                    ref={fromInputRef}
                    type="text"
                    value={from}
                    onChange={handleFromChange}
                    onClick={handleFromClick}
                    placeholder="City or airport"
                    className="location-input-bo"
                  />
                  {openDropdown === "from" && departSuggestions.length > 0 && (
                    <div className="suggestions-dropdown-bo">
                      {departSuggestions.map((airport) => (
                        <div
                          key={airport.id || airport.code}
                          className="suggestion-item-bo"
                          onClick={() =>
                            handleAirportSelect(airport, "departure")
                          }
                        >
                          <div className="suggestion-details-bo">
                            <div className="suggestion-name-bo">
                              {airport.name}
                            </div>
                            <div className="suggestion-code-bo">
                              {airport.code}
                            </div>
                            <div className="suggestion-location-bo">
                              {airport.city}, {airport.country}
                            </div>
                          </div>
                          <div className="suggestion-action-bo">Select</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <button
                className="swap-button-bo"
                onClick={swapLocations}
                title="Swap locations"
              >
                <IoSwapHorizontal />
              </button>

              <div className="location-input-wrapper-bo">
                <div className="input-header-bo">
                  <FiMapPin className="input-icon-bo" />
                  <span className="input-label-bo">To</span>
                  {toAirportCode && (
                    <span className="code-display-bo">{toAirportCode}</span>
                  )}
                </div>
                <div className="input-container-bo">
                  <input
                    ref={toInputRef}
                    type="text"
                    value={to}
                    onChange={handleToChange}
                    onClick={handleToClick}
                    placeholder="City or airport"
                    className="location-input-bo"
                  />
                  {openDropdown === "to" && arrivalSuggestions.length > 0 && (
                    <div className="suggestions-dropdown-bo">
                      {arrivalSuggestions.map((airport) => (
                        <div
                          key={airport.id || airport.code}
                          className="suggestion-item-bo"
                          onClick={() =>
                            handleAirportSelect(airport, "arrival")
                          }
                        >
                          <div className="suggestion-details-bo">
                            <div className="suggestion-name-bo">
                              {airport.name}
                            </div>
                            <div className="suggestion-code-bo">
                              {airport.code}
                            </div>
                            <div className="suggestion-location-bo">
                              {airport.city}, {airport.country}
                            </div>
                          </div>
                          <div className="suggestion-action-bo">Select</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Dates and Passengers */}
          <div className="form-row-bo second-row-bo">
            <div className="dates-group-bo">
              <div className="date-input-wrapper-bo" ref={departDateRef}>
                <div className="input-header-bo">
                  <FiCalendar className="input-icon-bo" />
                  <span className="input-label-bo">Depart</span>
                  {departDate && (
                    <span className="date-display-bo">
                      {departDate.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                  )}
                </div>
                <DatePicker
                  ref={departDatePickerRef}
                  selected={departDate}
                  onChange={handleDepartDateChange}
                  onCalendarOpen={handleDatePickerOpen}
                  dateFormat="MMM dd, yyyy"
                  customInput={<CustomInput />}
                  popperPlacement="bottom-start"
                  showPopperArrow={false}
                  wrapperClassName="date-picker-wrapper-bo"
                />
              </div>

              {tripType === "round" && (
                <div className="date-input-wrapper-bo" ref={returnDateRef}>
                  <div className="input-header-bo">
                    <FiCalendar className="input-icon-bo" />
                    <span className="input-label-bo">Return</span>
                    {returnDate && (
                      <span className="date-display-bo">
                        {returnDate.toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    )}
                  </div>
                  <DatePicker
                    ref={returnDatePickerRef}
                    selected={returnDate}
                    onChange={handleReturnDateChange}
                    onCalendarOpen={handleDatePickerOpen}
                    dateFormat="MMM dd, yyyy"
                    minDate={departDate ? addOneDay(departDate) : new Date()}
                    disabled={!departDate}
                    customInput={<CustomInput disabled={!departDate} />}
                    popperPlacement="bottom-start"
                    showPopperArrow={false}
                    wrapperClassName="date-picker-wrapper-bo"
                  />
                </div>
              )}
            </div>

            {/* UPDATED: Passenger Dropdown with Adults, Children, Infants */}
            <div className="passenger-dropdown-container-bo" ref={passengerDropdownRef}>
              <div className="detail-input-wrapper-bo">
                <div className="input-header-bo">
                  <FiUsers className="input-icon-bo" />
                  <span className="input-label-bo">Passengers</span>
                  {totalTravelers > 0 && (
                    <span className="count-display-bo">
                      {totalTravelers} {totalTravelers === 1 ? "traveler" : "travelers"}
                    </span>
                  )}
                </div>
                <div 
                  className="passenger-input-bo"
                  onClick={() => setIsPassengerDropdownOpen(!isPassengerDropdownOpen)}
                >
                  <span className="passenger-summary-bo">
                    {totalTravelers} {totalTravelers === 1 ? "Passenger" : "Passengers"}
                  </span>
                  <FiChevronDown className={`dropdown-arrow-bo ${isPassengerDropdownOpen ? 'rotated-bo' : ''}`} />
                </div>
                
                {isPassengerDropdownOpen && (
                  <div className="passenger-dropdown-bo">
                    <div className="passenger-option-bo">
                      <div className="passenger-type-bo">
                        <span className="type-label-bo">Adults</span>
                        <span className="type-description-bo">12+ Years</span>
                      </div>
                      <div className="passenger-counter-bo">
                        <button 
                          type="button"
                          className="counter-btn-bo"
                          onClick={(e) => {
                            e.stopPropagation();
                            updatePassengerCount('adults', 'decrease');
                          }}
                          disabled={passengerCount.adults <= 1}
                        >
                          <FiMinus />
                        </button>
                        <span className="counter-value-bo">{passengerCount.adults}</span>
                        <button 
                          type="button"
                          className="counter-btn-bo"
                          onClick={(e) => {
                            e.stopPropagation();
                            updatePassengerCount('adults', 'increase');
                          }}
                          disabled={passengerCount.adults + passengerCount.children + passengerCount.infants >= 9}
                        >
                          <FiPlus />
                        </button>
                      </div>
                    </div>
                    
                    <div className="passenger-option-bo">
                      <div className="passenger-type-bo">
                        <span className="type-label-bo">Child</span>
                        <span className="type-description-bo">2-11 Years</span>
                      </div>
                      <div className="passenger-counter-bo">
                        <button 
                          type="button"
                          className="counter-btn-bo"
                          onClick={(e) => {
                            e.stopPropagation();
                            updatePassengerCount('children', 'decrease');
                          }}
                          disabled={passengerCount.children <= 0}
                        >
                          <FiMinus />
                        </button>
                        <span className="counter-value-bo">{passengerCount.children}</span>
                        <button 
                          type="button"
                          className="counter-btn-bo"
                          onClick={(e) => {
                            e.stopPropagation();
                            updatePassengerCount('children', 'increase');
                          }}
                          disabled={passengerCount.adults + passengerCount.children + passengerCount.infants >= 9}
                        >
                          <FiPlus />
                        </button>
                      </div>
                    </div>
                    
                    <div className="passenger-option-bo">
                      <div className="passenger-type-bo">
                        <span className="type-label-bo">Infants</span>
                        <span className="type-description-bo">0-2 Years</span>
                      </div>
                      <div className="passenger-counter-bo">
                        <button 
                          type="button"
                          className="counter-btn-bo"
                          onClick={(e) => {
                            e.stopPropagation();
                            updatePassengerCount('infants', 'decrease');
                          }}
                          disabled={passengerCount.infants <= 0}
                        >
                          <FiMinus />
                        </button>
                        <span className="counter-value-bo">{passengerCount.infants}</span>
                        <button 
                          type="button"
                          className="counter-btn-bo"
                          onClick={(e) => {
                            e.stopPropagation();
                            updatePassengerCount('infants', 'increase');
                          }}
                          disabled={passengerCount.infants >= passengerCount.adults || passengerCount.adults + passengerCount.children + passengerCount.infants >= 9}
                        >
                          <FiPlus />
                        </button>
                      </div>
                    </div>
                    
                    <button 
                      type="button"
                      className="done-btn-bo"
                      onClick={() => setIsPassengerDropdownOpen(false)}
                    >
                      Done
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="search-button-wrapper-bo">
              <button
                className="search-button-bo"
                onClick={handleSearch}
                disabled={isSearching}
              >
                <FiSearch className="search-icon-bo" />
                {isSearching ? "Searching..." : "Search Flights"}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message-bo">
              <span className="error-icon-bo">!</span>
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

Book.defaultProps = {
  onSearch: null,
  initialSearchParams: null,
};

export default Book;